import {useDispatch, useSelector} from "react-redux";
import Breadcrumb from "../../../layout/breadcrumb";
import React, {Fragment, useEffect, useState} from "react";
import {Card, CardBody, Col, Container, Form, FormGroup, Input, Label, Row, Spinner} from "reactstrap";
import {Button} from "react-bootstrap";
import Select from "react-select";
import withReducer from "../../../store/withReducer";
import aiSettingsReducer from "./store/aiSettingsSlice";
import {getAiSetting, saveAiSetting, getGenerateImageOptions} from "./store/aiSettingsSlice";

const BACKGROUND_STYLES = ["beige studio", "light grey", "warm neutral", "editorial"];
const STYLING_MOODS = ["minimal", "luxury", "soft", "editorial"];

const SHOT_TYPE_LABELS = {
    fabric_roll: "Fabric Roll",
    fabric_fold: "Fabric Fold",
    fabric_drape: "Fabric Drape",
    fabric_hanging: "Fabric Hanging",
    still_life: "Still Life",
    macro: "Macro Close-up",
    model_wearing: "Model Wearing",
};

const AiSettings = () => {
    const dispatch = useDispatch();
    const {currentSetting, options, loading, saving} = useSelector(state => state.aiSettings || {});

    const [masterPrompt, setMasterPrompt] = useState("");
    const [backgroundStyle, setBackgroundStyle] = useState("beige studio");
    const [stylingMood, setStylingMood] = useState("minimal");
    const [prioritizeColorMatch, setPrioritizeColorMatch] = useState(true);
    const [defaultAspectRatio, setDefaultAspectRatio] = useState("1:1");
    const [selectedShotTypes, setSelectedShotTypes] = useState([]);
    const [isActive, setIsActive] = useState(true);

    useEffect(() => {
        dispatch(getAiSetting("generate_images"));
        dispatch(getGenerateImageOptions());
    }, [dispatch]);

    useEffect(() => {
        if (currentSetting) {
            setMasterPrompt(currentSetting.master_prompt || "");
            setIsActive(currentSetting.is_active ?? true);
            const s = currentSetting.settings || {};
            setBackgroundStyle(s.background_style || "beige studio");
            setStylingMood(s.styling_mood || "minimal");
            setPrioritizeColorMatch(s.prioritize_color_match ?? true);
            setDefaultAspectRatio(s.default_aspect_ratio || "1:1");
            const saved = s.shot_types || [];
            setSelectedShotTypes(saved.map(v => ({ value: v, label: SHOT_TYPE_LABELS[v] || v })));
        }
    }, [currentSetting]);

    const shotTypes = (options?.shot_types?.length > 0)
        ? options.shot_types
        : Object.keys(SHOT_TYPE_LABELS);

    const aspectRatios = (options?.aspect_ratios?.length > 0)
        ? options.aspect_ratios
        : ["1:1", "3:4", "4:3", "9:16", "16:9"];

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(saveAiSetting({
            feature: "generate_images",
            master_prompt: masterPrompt,
            settings: {
                background_style: backgroundStyle,
                styling_mood: stylingMood,
                prioritize_color_match: prioritizeColorMatch,
                default_aspect_ratio: defaultAspectRatio,
                shot_types: selectedShotTypes.map(s => s.value),
            },
            is_active: isActive,
        }));
    };

    const dataMenu = [
        {type: 0, text: "AI Settings"},
    ];

    if (loading) {
        return (
            <Fragment>
                <Breadcrumb title="AI Settings" data={dataMenu}/>
                <Container fluid={true}>
                    <div className="d-flex justify-content-center align-items-center" style={{minHeight: 300}}>
                        <Spinner/>
                    </div>
                </Container>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumb title="AI Settings" data={dataMenu}/>
            <Container fluid={true} className="product-wrapper">
                <Col sm="12">
                    <Card>
                        <CardBody>
                            <h5 className="mb-4">Generate Images</h5>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Col md={12}>
                                        <FormGroup>
                                            <Label className="col-form-label">Master Prompt</Label>
                                            <Input
                                                type="textarea"
                                                rows={8}
                                                placeholder="You are an expert luxury fashion textile photographer..."
                                                value={masterPrompt}
                                                onChange={(e) => setMasterPrompt(e.target.value)}
                                                style={{border: '1px solid gray'}}
                                            />
                                            <small className="text-muted">
                                                This prompt is combined with product data and sent to the AI model for image generation.
                                            </small>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="col-form-label">Background Style</Label>
                                            <Input
                                                type="select"
                                                value={backgroundStyle}
                                                onChange={(e) => setBackgroundStyle(e.target.value)}
                                                style={{border: '1px solid gray'}}
                                            >
                                                {BACKGROUND_STYLES.map((s) => (
                                                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="col-form-label">Styling Mood</Label>
                                            <Input
                                                type="select"
                                                value={stylingMood}
                                                onChange={(e) => setStylingMood(e.target.value)}
                                                style={{border: '1px solid gray'}}
                                            >
                                                {STYLING_MOODS.map((s) => (
                                                    <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="col-form-label">Shot Types</Label>
                                            <Select
                                                isMulti={true}
                                                getOptionLabel={(option) => option.label}
                                                getOptionValue={(option) => option.value}
                                                value={selectedShotTypes}
                                                menuPortalTarget={document.body}
                                                styles={{menuPortal: base => ({...base, zIndex: 9999})}}
                                                onChange={(e) => setSelectedShotTypes(e || [])}
                                                placeholder="Select shot types..."
                                                options={shotTypes.map(s => ({value: s, label: SHOT_TYPE_LABELS[s] || s}))}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup>
                                            <Label className="col-form-label">Default Aspect Ratio</Label>
                                            <Input
                                                type="select"
                                                value={defaultAspectRatio}
                                                onChange={(e) => setDefaultAspectRatio(e.target.value)}
                                                style={{border: '1px solid gray'}}
                                            >
                                                {aspectRatios.map((r) => (
                                                    <option key={r} value={r}>{r}</option>
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <FormGroup check className="mt-3">
                                            <Label check>
                                                <Input
                                                    type="checkbox"
                                                    checked={prioritizeColorMatch}
                                                    onChange={(e) => setPrioritizeColorMatch(e.target.checked)}
                                                />{" "}
                                                Prioritize Color Match
                                            </Label>
                                            <small className="text-muted d-block mt-1">
                                                When enabled, the AI will prioritize accurate fabric color reproduction.
                                            </small>
                                        </FormGroup>
                                    </Col>
                                    <Col md={6}>
                                        <FormGroup check className="mt-3">
                                            <Label check>
                                                <Input
                                                    type="checkbox"
                                                    checked={isActive}
                                                    onChange={(e) => setIsActive(e.target.checked)}
                                                />{" "}
                                                Active
                                            </Label>
                                            <small className="text-muted d-block mt-1">
                                                Enable or disable AI image generation globally.
                                            </small>
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={10}></Col>
                                    <Col md={2} style={{marginTop: 36}}>
                                        <button
                                            style={{border: '1px solid black'}}
                                            className="btn btn-success btn-block"
                                            type="submit"
                                            disabled={saving}
                                        >
                                            {saving ? <Spinner size="sm"/> : "Save"}
                                        </button>
                                    </Col>
                                </Row>
                            </Form>
                        </CardBody>
                    </Card>
                </Col>
            </Container>
        </Fragment>
    );
};

export default withReducer([{aiSettings: aiSettingsReducer}])(AiSettings);
